import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import CurrencyForm from '../../components/Form/CurrencyForm';
import { Currency } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCurrencies, useCreateCurrency, useUpdateCurrency, useDeleteCurrency } from '../../hooks/useCurrencies';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';

const Currencies: React.FC = () => {
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'code', order: 'asc' as const }]
  }), []);

  const { data: currencies, isLoading, error, refetch } = useCurrencies(queryOptions);

  const createMutation = useCreateCurrency(() => {
    refetch();
    alert('Currency added successfully!');
  });

  const updateMutation = useUpdateCurrency(() => {
    refetch();
    alert('Currency updated successfully!');
  });

  const deleteMutation = useDeleteCurrency(() => {
    refetch();
    alert('Currency deleted successfully!');
  });

  const [showForm, setShowForm] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | undefined>();
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddCurrency = async (data: Omit<Currency, 'id' | 'createdAt' | 'updatedAt' | 'lastUpdated'>) => {
    const currencyData = {
      ...data,
      lastUpdated: new Date().toISOString()
    };
    await createMutation.execute(currencyData);
    setShowForm(false);
  };

  const handleEditCurrency = async (data: Omit<Currency, 'id' | 'createdAt' | 'updatedAt' | 'lastUpdated'>) => {
    if (editingCurrency) {
      const updateData = {
        ...data,
        documentId: editingCurrency.documentId,
        lastUpdated: new Date().toISOString()
      };
      await updateMutation.execute({ id: editingCurrency.id, data: updateData });
      setEditingCurrency(undefined);
      setShowForm(false);
    }
  };

  const handleDeleteCurrency = async (currency: Currency) => {
    if (window.confirm('Are you sure you want to delete this currency?')) {
      await deleteMutation.execute(currency);
    }
  };

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCurrency(undefined);
  };

  const handleExport = () => {
    const exportData = filteredCurrencies.map(currency => ({
      'Code': currency.code,
      'Name': currency.name,
      'Symbol': currency.symbol,
      'Exchange Rate to EGP': currency.exchangeRateToEGP,
      'Status': currency.isActive ? 'Active' : 'Inactive',
      'Last Updated': new Date(currency.lastUpdated).toLocaleString(),
      'Created': new Date(currency.createdAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `currencies_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredCurrencies.map(currency => ({
      'Code': currency.code,
      'Name': currency.name,
      'Symbol': currency.symbol,
      'Exchange Rate to EGP': currency.exchangeRateToEGP,
      'Status': currency.isActive ? 'Active' : 'Inactive',
      'Last Updated': new Date(currency.lastUpdated).toLocaleString(),
      'Created': new Date(currency.createdAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Currencies');
  };

  const columns = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (value: string) => (
        <span className="font-semibold text-gray-900">{value}</span>
      )
    },
    { key: 'name', label: 'Currency Name', sortable: true },
    {
      key: 'symbol',
      label: 'Symbol',
      render: (value: string) => (
        <span className="text-lg font-bold text-ocean-600">{value}</span>
      )
    },
    {
      key: 'exchangeRateToEGP',
      label: 'Exchange Rate (to EGP)',
      sortable: true,
      render: (value: number) => (
        <span className="font-medium text-gray-900">{value.toFixed(4)}</span>
      )
    },
    {
      key: 'lastUpdated',
      label: 'Last Updated',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-gray-600">
          {new Date(value).toLocaleDateString()} {new Date(value).toLocaleTimeString()}
        </span>
      )
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, item: Currency) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(item)}
            className="text-blue-600 hover:text-blue-900 font-medium"
          >
            Edit
          </button>
          <button
            onClick={() => handleDeleteCurrency(item)}
            className="text-red-600 hover:text-red-900 font-medium"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const filteredCurrencies = useMemo(() => {
    if (!currencies || currencies.length === 0) return [];
    return currencies.filter(currency =>
      currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [currencies, searchTerm]);

  const filterConfigs: never[] = [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Currency Management"
        description="Manage exchange rates and currency settings"
      />

      {showForm && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingCurrency ? 'Edit Currency' : 'Add New Currency'}
          </h2>
          <CurrencyForm
            onSubmit={editingCurrency ? handleEditCurrency : handleAddCurrency}
            initialData={editingCurrency}
            onCancel={handleCancel}
          />
        </div>
      )}

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by code, name, or symbol..."
        filters={filterConfigs}
        onClearFilters={() => setSearchTerm('')}
        totalCount={currencies?.length || 0}
        filteredCount={filteredCurrencies.length}
        entityName="currencies"
        onAdd={!showForm ? () => setShowForm(true) : undefined}
        addLabel="Add Currency"
        onExport={handleExport}
        onPrint={handlePrint}
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Exchange Rate Information</p>
            <p>Exchange rates are relative to Egyptian Pound (EGP). Update rates regularly to reflect current market conditions. Rates affect invoices, bills, and financial transactions throughout the system.</p>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <ErrorState
            message={error.message || String(error)}
            onRetry={refetch}
            entityName="currencies"
          />
        ) : (
          <Table data={filteredCurrencies} columns={columns} />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Currencies</div>
          <div className="text-2xl font-bold text-gray-900">{currencies?.length || 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Active Currencies</div>
          <div className="text-2xl font-bold text-green-600">
            {currencies?.filter(c => c.isActive).length || 0}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Inactive Currencies</div>
          <div className="text-2xl font-bold text-red-600">
            {currencies?.filter(c => !c.isActive).length || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Currencies;
